import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import fetchWithAuth from '../../utils/fetchWithAuth';
import { useAuth } from '../../context/AuthContext';

import WelcomeScreen from './WelcomeScreen';
import NavigationTour from './NavigationTour';
import Paywall from '../../pages/Paywall';

export type Placement = 'top' | 'bottom' | 'mid';

export type Step = {
  path: string;       // relative to /app/onboarding
  elementId?: string;  // ID to anchor tooltip
  text: string;       // tooltip copy
  placement?: Placement; 
};

const STEPS: Step[] = [
  {
    path: 'home',
    elementId: 'nav-home',
    text: 'This is your Home page: Hand-picked recipes just for you. This page will become more personalized over time',
    placement: 'bottom', 
  },
  {
    path: 'search',
    elementId: 'nav-search',
    text: 'Here’s Search Page: Look up recipes by name, ingredients, or various filters like Vegan, Low Sugar, Cheap, etc. Type in what you got in the fridge one by one and we’ll show you what you can make',
    placement: 'top', 
  },
  {
    path: 'recipe/37609e11-231b-445d-8099-cd6bf89de3e1',
    text: 'Tap “Add to Meal Plan” to schedule as many servings of any recipe for upcoming days',
    placement: 'bottom', 
  },
  {
    path: 'shopping-list',
    elementId: 'shopping-list-header',
    text: 'Tap “Create Shopping List” and turn your planned meals into a shopping list with one click.',
    placement: 'bottom', 
  },
  {
    path: 'recipe/37609e11-231b-445d-8099-cd6bf89de3e1',
    elementId: 'nutrition-section',
    text: 'Unlock full nutrition facts, allergy alerts, diet filters and more with PanPals Premium.',
    placement: 'mid', 
  },
];

export default function Onboarding() {
  const [step, setStep] = useState(0);   // 0 = welcome, 1…STEPS.length = tour, last = premium teaser
  const navigate = useNavigate();
  const { refreshBackendUser } = useAuth();

  // helper to mark onboarding done on server, then update context
  const finishOnboarding = async () => {
    await fetchWithAuth('/api/users/onboarding-complete', { method: 'POST' });
    await refreshBackendUser();
  };

  // Whenever we enter a new tour step, navigate to its child route,
  // and when we hit the final step, redirect into the “real” paywall page.
  useEffect(() => {
    if (step > 0 && step <= STEPS.length) {
      navigate(
        `/app/onboarding/${STEPS[step - 1].path}`,
        { replace: true, state: { onboardingStep: step } }
      );
    } else if (step === STEPS.length + 1) {
      // LAST STEP: first mark onboarding done, then jump to the real paywall route
      finishOnboarding()
        .catch(console.error)
        .then(() => {
          navigate('/app/subscribe', {
            replace: true,
            state: { fromOnboarding: true },
          });
        });
    }
  }, [step, navigate]);

  const next = () => setStep((s) => s + 1);

  return (
    <>
      {/* Always render the nested child (Home, Search, etc.) */}
      <Outlet />

      {/* Step 0: Welcome screen */}
      {step === 0 && <WelcomeScreen onNext={next} />}

      {/* Steps 1..N: Tooltip tour */}
      {step > 0 && step <= STEPS.length && (
        <NavigationTour
          stepIndex={step - 1}
          tourSteps={STEPS}
          onNext={next}
        />
      )}

      {/* Final step: Paywall */}
      {step === STEPS.length + 1 && (
        <Paywall
          isOnboarding
          // “Maybe Later”: finish onboarding and go home
          onBack={async () => {
            await finishOnboarding();
            navigate('/app/home', { replace: true });
          }}
          // after successful purchase: finish onboarding and show subscription
          onSubscribe={async () => {
            await finishOnboarding();
            navigate('/app/settings/subscription', { replace: true });
          }}
        />
      )}
    </>
  );
}
